import { ValueObject } from '../shared/ValueObject';
import { ValidationError } from '../errors/ValidationError';

/**
 * ユーザーID値オブジェクト
 *
 * UUID v4形式のユーザー識別子
 * データベースのVARCHAR(36)型に対応
 *
 * 不変条件:
 * - UUID v4形式であること（xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx）
 * - 36文字の固定長
 */
export class UserId extends ValueObject<string> {
  // UUID v4の正規表現パターン
  private static readonly UUID_V4_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  private static readonly UUID_LENGTH = 36;

  private constructor(value: string) {
    super(value);
  }

  /**
   * UserIdを生成
   * @throws {ValidationError} 不正な値の場合
   */
  public static create(value: string): UserId {
    UserId.validate(value);
    return new UserId(value.toLowerCase()); // 小文字に正規化
  }

  /**
   * 新しいUUID v4を生成してUserIdを作成
   */
  public static generate(): UserId {
    const uuid = UserId.generateUuidV4();
    return new UserId(uuid);
  }

  /**
   * プリミティブ型から生成（後方互換性のため）
   */
  public static fromPrimitive(value: string): UserId {
    return UserId.create(value);
  }

  /**
   * プリミティブ型に変換（後方互換性のため）
   */
  public toPrimitive(): string {
    return this.value;
  }

  private static validate(value: string): void {
    if (!value || value.trim() === '') {
      throw new ValidationError('ユーザーIDは必須です');
    }

    if (value.length !== UserId.UUID_LENGTH) {
      throw new ValidationError(
        `ユーザーIDは${UserId.UUID_LENGTH}文字である必要があります`
      );
    }

    if (!UserId.UUID_V4_REGEX.test(value)) {
      throw new ValidationError(
        'ユーザーIDは有効なUUID v4形式である必要があります'
      );
    }
  }

  /**
   * UUID v4を生成
   * crypto.randomUUID()が利用可能な場合はそれを使用、
   * そうでない場合は簡易実装
   */
  private static generateUuidV4(): string {
    // Node.js 14.17.0以降、もしくはブラウザ環境でcrypto.randomUUID()が利用可能
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // フォールバック実装（簡易版）
    const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
    return template.replace(/[xy]/g, (char) => {
      const random = (Math.random() * 16) | 0;
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  /**
   * 同一性の確認
   */
  public isSameAs(other: UserId | null | undefined): boolean {
    return this.equals(other);
  }

  /**
   * ダッシュを除いた形式で取得
   */
  public toCompactString(): string {
    return this.value.replace(/-/g, '');
  }

  public toString(): string {
    return this.value;
  }

  public toJSON(): string {
    return this.value;
  }

  /**
   * データベース保存用の値を取得
   */
  public toDatabaseValue(): string {
    return this.value;
  }
}